<template>
  <v-main>
    <loader :loading="loading"></loader>
    <v-container fluid class="em-page">
      <!-- Breadcrumbs -->
      <div class="breadcrumbs-container">
        <Breadcrumbs />
      </div>

      <!-- Sub-Claim Context Banner -->
      <div class="em-context-banner" v-if="subClaim">
        <div class="em-context-accent"></div>
        <div class="em-context-content">
          <div class="em-context-left">
            <v-avatar size="42" class="em-context-avatar">
              <v-icon size="22" color="white">mdi-shield-check-outline</v-icon>
            </v-avatar>
            <div class="em-context-info">
              <span class="em-context-label">Mapping evidence to sub-claim</span>
              <span class="em-context-title">{{
                subClaim.title || subClaim.name || 'Sub-Claim #' + subClaim.id
              }}</span>
            </div>
          </div>
          <div class="em-context-right">
            <div class="em-context-steps">
              <div class="em-step em-step-active">
                <v-icon size="16" color="#1a237e">mdi-checkbox-marked-circle</v-icon>
                <span>Browse</span>
              </div>
              <v-icon size="14" color="#bdbdbd" class="em-step-arrow">mdi-chevron-right</v-icon>
              <div class="em-step" :class="{ 'em-step-active': selectedItems.length > 0 }">
                <v-icon size="16" :color="selectedItems.length > 0 ? '#00695c' : '#bdbdbd'">
                  {{
                    selectedItems.length > 0 ? 'mdi-checkbox-marked-circle' : 'mdi-circle-outline'
                  }}
                </v-icon>
                <span>Select</span>
              </div>
              <v-icon size="14" color="#bdbdbd" class="em-step-arrow">mdi-chevron-right</v-icon>
              <div
                class="em-step"
                :class="{ 'em-step-active': !hasUnsavedChanges && selectedItems.length > 0 }"
              >
                <v-icon
                  size="16"
                  :color="!hasUnsavedChanges && selectedItems.length > 0 ? '#00695c' : '#bdbdbd'"
                >
                  {{
                    !hasUnsavedChanges && selectedItems.length > 0
                      ? 'mdi-checkbox-marked-circle'
                      : 'mdi-circle-outline'
                  }}
                </v-icon>
                <span>Save</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Two-Panel Workspace -->
      <div class="em-workspace">
        <!-- LEFT: Evidence Catalog -->
        <div class="em-catalog-panel">
          <div class="em-panel-label">
            <v-icon size="18" color="#1a237e" class="mr-2">mdi-file-search-outline</v-icon>
            <span>Browse &amp; Select Evidence</span>
            <v-chip x-small class="ml-2 em-hint-chip" v-if="selectedItems.length === 0">
              Toggle the switch on cards to select
            </v-chip>
          </div>
          <v-card class="em-catalog-card" elevation="1">
            <EvidenceList
              :key="evidenceListKey"
              :mapper="true"
              :disabled-items="transferredItems"
              @evidence-selected="handleEvidenceSelected"
            />
          </v-card>
        </div>

        <!-- RIGHT: Mapping Sidebar (Sticky) -->
        <div class="em-mapping-panel">
          <div class="em-mapping-sticky">
            <!-- Sidebar Header -->
            <div class="em-mapping-header">
              <div class="em-mapping-header-left">
                <v-icon size="20" color="white" class="mr-2">mdi-link-variant</v-icon>
                <span class="em-mapping-header-title">{{ $t('evidenceMapper.tableTitle') }}</span>
              </div>
              <v-chip small dark class="em-mapping-count-chip" v-if="selectedItems.length > 0">
                {{ selectedItems.length }}
              </v-chip>
            </div>

            <!-- Stats Row -->
            <div class="em-mapping-stats" v-if="selectedItems.length > 0">
              <div class="em-stat-item">
                <span class="em-stat-value">{{ selectedItems.length }}</span>
                <span class="em-stat-label">Evidence</span>
              </div>
              <div class="em-stat-separator"></div>
              <div class="em-stat-item">
                <span class="em-stat-value">{{ evidenceTypes.length }}</span>
                <span class="em-stat-label">Types</span>
              </div>
            </div>

            <!-- Mapped Items Scrollable List -->
            <div class="em-mapping-items-scroll">
              <!-- Empty state -->
              <div v-if="selectedItems.length === 0" class="em-mapping-empty">
                <div class="em-mapping-empty-icon-wrap">
                  <v-icon size="36" color="#c5cae9">mdi-file-document-plus-outline</v-icon>
                </div>
                <p class="em-mapping-empty-title">No evidence mapped yet</p>
                <p class="em-mapping-empty-hint">
                  Toggle the switch on evidence cards in the left panel to add them here
                </p>
              </div>

              <!-- Mapped items with transitions -->
              <transition-group name="em-item" tag="div" class="em-mapped-items-list" v-else>
                <div v-for="item in selectedItems" :key="item.id" class="em-mapped-item">
                  <div class="em-mapped-item-top">
                    <v-chip x-small outlined color="#1a237e" class="em-mapped-item-id">
                      #{{ item.id }}
                    </v-chip>
                    <v-btn
                      icon
                      x-small
                      class="em-mapped-item-remove"
                      @click="toggleSelection(item, 'selected')"
                      title="Remove from mapping"
                    >
                      <v-icon size="16">mdi-close</v-icon>
                    </v-btn>
                  </div>
                  <p class="em-mapped-item-title">{{ item.title }}</p>
                  <div class="em-mapped-item-bottom">
                    <v-chip x-small class="em-mapped-item-type">{{ item.type }}</v-chip>
                  </div>
                </div>
              </transition-group>
            </div>

            <!-- Unsaved Changes Notice -->
            <div class="em-unsaved-notice" v-if="hasUnsavedChanges">
              <v-icon size="14" color="#e65100" class="mr-1">mdi-alert-circle</v-icon>
              <span>You have unsaved changes</span>
            </div>

            <!-- Save Action Bar -->
            <div class="em-mapping-action">
              <v-tooltip top>
                <template #activator="{ on, attrs }">
                  <v-btn
                    class="em-save-btn"
                    block
                    large
                    dark
                    v-bind="attrs"
                    v-on="on"
                    @click="updateSubClaim()"
                    :disabled="!hasUnsavedChanges && selectedItems.length === 0"
                    :loading="saving"
                  >
                    <v-icon left size="20">mdi-content-save-check-outline</v-icon>
                    {{ $t('evidenceMapper.mapButton') }}
                    <span v-if="selectedItems.length > 0" class="ml-1"
                      >({{ selectedItems.length }})</span
                    >
                  </v-btn>
                </template>
                <span>{{ $t('evidenceMapper.toolTip') }}</span>
              </v-tooltip>
            </div>
          </div>
        </div>
      </div>
    </v-container>
  </v-main>
</template>

<script>
import EvidenceList from '@/modules/evidence/views/EvidenceList';
import Breadcrumbs from '@/components/charts/Breadcrumbs.vue';
import Loader from '@/components/helpers/Loader';
import subClaimsApi from '../api/subClaimsApi';
import EventBus from '@/helpers/event-bus.js';

export default {
  name: 'SubClaimEvidenceMapper',
  components: {
    EvidenceList,
    Breadcrumbs,
    Loader,
  },
  data() {
    return {
      availableItems: [],
      selectedItems: [],
      selectedAvailable: [],
      selectedEvidence: [],
      transferredItems: [],
      initialMappedIds: [],
      loading: false,
      saving: false,
      evidenceListKey: 0,
      subClaim: JSON.parse(localStorage.getItem('subClaim')),
    };
  },
  computed: {
    hasUnsavedChanges() {
      const currentIds = this.selectedItems
        .map(i => i.id)
        .slice()
        .sort();
      const initialIds = this.initialMappedIds.slice().sort();
      if (currentIds.length !== initialIds.length) return true;
      return currentIds.some((id, idx) => id !== initialIds[idx]);
    },
    evidenceTypes() {
      return [...new Set(this.selectedItems.map(item => item.type).filter(Boolean))];
    },
  },
  created() {
    this.findMappedEvidence();
  },
  methods: {
    async findMappedEvidence() {
      try {
        this.loading = true;
        const { data: subClaim } = await subClaimsApi.getSubClaimById(this.subClaim.id);
        this.selectedItems = subClaim.evidences || [];
        this.transferredItems = this.selectedItems.map(item => item.id);
        this.initialMappedIds = this.selectedItems.map(item => item.id);
      } catch (error) {
        if (error.response && error.response.status === 400) {
          EventBus.$emit('snackbar', {
            text: this.$t('errorList.error-404'),
            color: 'grey',
          });
        }
      } finally {
        this.loading = false;
      }
    },
    async updateSubClaim() {
      try {
        this.saving = true;
        this.subClaim.evidences = this.selectedItems;
        await subClaimsApi.update(this.subClaim);
        // Update the baseline so unsaved indicator clears
        this.initialMappedIds = this.selectedItems.map(item => item.id);
        EventBus.$emit('snackbar', {
          text: this.$t('Evidences successfully mapped'),
          color: 'green',
        });
        // Force re-render EvidenceList to refresh its data without a full page reload
        this.evidenceListKey += 1;
      } catch (error) {
        if (error.response && error.response.status === 400) {
          EventBus.$emit('snackbar', {
            text: this.$t('errorList.error-404'),
            color: 'grey',
          });
        }
      } finally {
        this.saving = false;
      }
    },
    handleEvidenceSelected(items) {
      // Only store the items that are explicitly selected
      this.selectedAvailable = items.filter(
        item =>
          // Make sure the item isn't already in the selected items list
          !this.selectedItems.some(selected => selected.id === item.id),
      );

      // Only move items if there are items to move
      if (this.selectedAvailable.length > 0) {
        this.moveSelected('available');
      }
    },
    toggleSelection(item, list) {
      const selectedList = list === 'available' ? 'selectedAvailable' : 'selectedEvidence';
      if (this[selectedList].includes(item)) {
        this[selectedList] = this[selectedList].filter(i => i.id !== item.id);
      } else {
        this[selectedList].push(item);
        if (list !== 'available') {
          this.moveSelected('selectedEvidence');
        }
      }
    },
    moveSelected(list) {
      if (list === 'available') {
        // Filter out any items that are already in selectedItems to prevent duplicates
        const newItems = this.selectedAvailable.filter(
          item => !this.selectedItems.some(selected => selected.id === item.id),
        );

        if (newItems.length > 0) {
          this.selectedItems.push(...newItems);
          // Only mark the new items as transferred
          this.transferredItems.push(...newItems.map(item => item.id));
        }
        this.selectedAvailable = [];
      } else {
        // Remove selected evidence from the list
        this.selectedItems = this.selectedItems.filter(
          item => !this.selectedEvidence.includes(item),
        );
        // Remove from transferred
        this.transferredItems = this.transferredItems.filter(
          id => !this.selectedEvidence.some(item => item.id === id),
        );
        this.selectedEvidence = [];
      }
    },
  },
};
</script>

<style>
/* ============================================
   EVIDENCE MAPPER — Split-Panel Design System
   ============================================ */

/* ─── Page Container ─── */
.em-page {
  background: #f4f6f9;
  min-height: 100vh;
  padding-bottom: 40px;
}

/* ─── Breadcrumbs ─── */
.breadcrumbs-container {
  display: flex;
  justify-content: center;
  align-items: center;
  padding-top: 16px;
  padding-bottom: 20px;
}

.breadcrumbs-container .v-breadcrumbs {
  background-color: #ffffff;
  padding: 10px 20px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
}

/* ─── Context Banner ─── */
.em-context-banner {
  background: #ffffff;
  border-radius: 12px;
  margin-bottom: 24px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  position: relative;
}

.em-context-accent {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: linear-gradient(90deg, #1a237e 0%, #00695c 100%);
}

.em-context-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 24px;
  flex-wrap: wrap;
  gap: 12px;
}

.em-context-left {
  display: flex;
  align-items: center;
  gap: 14px;
}

.em-context-avatar {
  background: linear-gradient(135deg, #1a237e 0%, #283593 100%) !important;
  flex-shrink: 0;
}

.em-context-info {
  display: flex;
  flex-direction: column;
}

.em-context-label {
  font-size: 0.72rem;
  color: #757575;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: 600;
}

.em-context-title {
  font-size: 1.1rem;
  font-weight: 700;
  color: #1a237e;
  line-height: 1.3;
}

.em-context-right {
  display: flex;
  align-items: center;
}

.em-context-steps {
  display: flex;
  align-items: center;
  gap: 4px;
}

.em-step {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 5px 12px;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  color: #9e9e9e;
  background: #f5f5f5;
  transition: all 0.3s ease;
}

.em-step-active {
  color: #1a237e;
  background: #e8eaf6;
}

.em-step-arrow {
  flex-shrink: 0;
}

/* ─── Two-Panel Workspace ─── */
.em-workspace {
  display: flex;
  gap: 24px;
  align-items: flex-start;
}

/* Left Panel: Evidence Catalog */
.em-catalog-panel {
  flex: 1;
  min-width: 0;
}

.em-panel-label {
  display: flex;
  align-items: center;
  margin-bottom: 12px;
  font-size: 0.82rem;
  font-weight: 700;
  color: #1a237e;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.em-hint-chip {
  background: #e8eaf6 !important;
  color: #3949ab !important;
  font-weight: 500 !important;
  font-size: 0.68rem !important;
  text-transform: none !important;
}

.em-catalog-card {
  border-radius: 12px !important;
  border: 1px solid #e0e0e0 !important;
  overflow: hidden;
}

/* ─── Right Panel: Mapping Sidebar ─── */
.em-mapping-panel {
  width: 360px;
  margin-top: 30px;
  flex-shrink: 0;
}

.em-mapping-sticky {
  position: sticky;
  top: 80px;
  display: flex;
  flex-direction: column;
  max-height: calc(100vh - 100px);
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.08);
  border: 1px solid #e0e0e0;
  overflow: hidden;
}

/* Sidebar Header */
.em-mapping-header {
  background: linear-gradient(135deg, #00695c 0%, #00897b 100%);
  padding: 14px 18px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;
}

.em-mapping-header-left {
  display: flex;
  align-items: center;
}

.em-mapping-header-title {
  color: white;
  font-size: 0.9rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.em-mapping-count-chip {
  background: rgba(255, 255, 255, 0.25) !important;
  backdrop-filter: blur(4px);
  font-weight: 700 !important;
  min-width: 28px;
  justify-content: center;
}

/* Stats Row */
.em-mapping-stats {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px 18px;
  background: #f5f5f5;
  border-bottom: 1px solid #eeeeee;
  flex-shrink: 0;
}

.em-stat-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
}

.em-stat-value {
  font-size: 1.25rem;
  font-weight: 800;
  color: #00695c;
  line-height: 1;
}

.em-stat-label {
  font-size: 0.65rem;
  color: #757575;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-top: 2px;
  font-weight: 600;
}

.em-stat-separator {
  width: 1px;
  height: 28px;
  background: #e0e0e0;
  margin: 0 12px;
}

/* ─── Mapped Items Scrollable Area ─── */
.em-mapping-items-scroll {
  flex: 1;
  overflow-y: auto;
  padding: 12px;
  min-height: 180px;
}

.em-mapping-items-scroll::-webkit-scrollbar {
  width: 4px;
}

.em-mapping-items-scroll::-webkit-scrollbar-track {
  background: transparent;
}

.em-mapping-items-scroll::-webkit-scrollbar-thumb {
  background: #c5cae9;
  border-radius: 4px;
}

/* Empty State */
.em-mapping-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 32px 16px;
  text-align: center;
}

.em-mapping-empty-icon-wrap {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: #e8eaf6;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 14px;
}

.em-mapping-empty-title {
  font-size: 0.9rem;
  font-weight: 700;
  color: #616161;
  margin: 0 0 6px 0;
}

.em-mapping-empty-hint {
  font-size: 0.76rem;
  color: #9e9e9e;
  line-height: 1.5;
  margin: 0;
  max-width: 240px;
}

/* ─── Mapped Item Cards ─── */
.em-mapped-items-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.em-mapped-item {
  background: #ffffff;
  border: 1px solid #e8eaf6;
  border-left: 3px solid #00695c;
  border-radius: 8px;
  padding: 10px 12px;
  transition: all 0.2s ease;
}

.em-mapped-item:hover {
  border-color: #c5cae9;
  border-left-color: #1a237e;
  box-shadow: 0 2px 8px rgba(26, 35, 126, 0.08);
}

.em-mapped-item-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 4px;
}

.em-mapped-item-id {
  font-weight: 700 !important;
  font-size: 0.7rem !important;
}

.em-mapped-item-remove {
  opacity: 0.35;
  transition: all 0.2s ease;
}

.em-mapped-item:hover .em-mapped-item-remove {
  opacity: 1;
}

.em-mapped-item-remove:hover {
  color: #d32f2f !important;
  background: rgba(211, 47, 47, 0.08) !important;
}

.em-mapped-item-title {
  font-size: 0.82rem;
  font-weight: 600;
  color: #37474f;
  line-height: 1.4;
  margin: 0 0 6px 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.em-mapped-item-bottom {
  display: flex;
  align-items: center;
}

.em-mapped-item-type {
  background: #f5f5f5 !important;
  color: #616161 !important;
  font-size: 0.68rem !important;
  font-weight: 600 !important;
}

/* ─── Transition Animations ─── */
.em-item-enter-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.em-item-leave-active {
  transition: all 0.25s cubic-bezier(0.4, 0, 1, 1);
}

.em-item-enter,
.em-item-leave-to {
  opacity: 0;
  transform: translateX(-16px);
}

.em-item-move {
  transition: transform 0.3s ease;
}

/* ─── Unsaved Changes Notice ─── */
.em-unsaved-notice {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px 14px;
  background: #fff3e0;
  border-top: 1px solid #ffe0b2;
  font-size: 0.75rem;
  font-weight: 600;
  color: #bf360c;
  flex-shrink: 0;
}

/* ─── Save Action Bar ─── */
.em-mapping-action {
  padding: 14px;
  border-top: 1px solid #e0e0e0;
  background: #fafafa;
  flex-shrink: 0;
}

.em-save-btn {
  background: linear-gradient(135deg, #1a237e 0%, #283593 100%) !important;
  text-transform: none !important;
  font-weight: 700 !important;
  letter-spacing: 0.3px !important;
  border-radius: 10px !important;
  font-size: 0.9rem !important;
  box-shadow: 0 4px 16px rgba(26, 35, 126, 0.25) !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
}

.em-save-btn:not(.v-btn--disabled):hover {
  box-shadow: 0 6px 24px rgba(26, 35, 126, 0.35) !important;
  transform: translateY(-1px);
}

/* ─── Responsive Breakpoints ─── */
@media (max-width: 1200px) {
  .em-mapping-panel {
    width: 320px;
  }
}

@media (max-width: 960px) {
  .em-workspace {
    flex-direction: column;
  }

  .em-catalog-panel {
    width: 100%;
  }

  .em-mapping-panel {
    width: 100%;
  }

  .em-mapping-sticky {
    position: relative;
    top: auto;
    max-height: none;
  }

  .em-mapping-items-scroll {
    max-height: 350px;
  }

  .em-context-steps {
    display: none;
  }
}

@media (max-width: 600px) {
  .em-context-content {
    padding: 12px 16px;
  }

  .em-context-title {
    font-size: 0.95rem;
  }

  .em-mapping-panel {
    width: 100%;
  }

  .em-panel-label {
    font-size: 0.75rem;
  }
}
</style>
